/**
 * Layout - Main layout wrapper with header and optional sidebar.
 * Used for both landing page and documentation pages.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AccountMenu from "./AccountMenu";
import DocsLanguageSwitch from "./DocsLanguageSwitch";
import SepmLogo from "./SepmLogo";
import ThemeButton from "./ThemeButton";
import { siteCopy } from "../i18n/siteCopy";
import { useManualLocale } from "../manual/i18n/LocaleContext";

export default function Layout({ children, sidebar, showSidebar = false }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { locale } = useManualLocale();
  const copy = siteCopy[locale];

  useEffect(() => {
    document.title = copy.documentTitle;
  }, [copy.documentTitle]);

  return (
    <div className="min-h-screen flex flex-col bg-gray-100 dark:bg-gray-900 transition-colors">
      {/* Header - fixed, responsive */}
      <header className="sticky top-0 z-40 bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between h-14 px-4 md:px-6">
          <div className="flex items-center gap-4">
            {/* Mobile menu toggle - only when sidebar exists */}
            {showSidebar && (
              <button
                onClick={() => setSidebarOpen((o) => !o)}
                className="md:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                aria-label="Toggle sidebar"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            )}
            <Link to="/" className="flex min-w-0 items-center gap-2.5">
              <SepmLogo className="h-9 w-9 shrink-0" />
              <span className="truncate font-semibold text-gray-900 dark:text-gray-100">
                <span className="text-sepm-primary dark:text-sepm-primary-dark">SEPM</span>
                <span className="hidden sm:inline"> {copy.documentation}</span>
              </span>
            </Link>
          </div>

          <div className="flex items-center gap-2">
          <DocsLanguageSwitch />
          <AccountMenu />
          <ThemeButton />
          </div>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar - collapsible on mobile */}
        {showSidebar && sidebar && (
          <aside
            className={`
              fixed md:sticky top-14 left-0 z-30 h-[calc(100vh-3.5rem)]
              w-64 flex-shrink-0 overflow-y-auto
              bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700
              transform transition-transform duration-200 ease-in-out
              ${sidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}
            `}
          >
            <div className="p-4">{sidebar}</div>
            {/* Overlay on mobile when sidebar open */}
            {sidebarOpen && (
              <div
                className="fixed inset-0 bg-black/50 md:hidden z-[-1]"
                onClick={() => setSidebarOpen(false)}
                aria-hidden="true"
              />
            )}
          </aside>
        )}

        {/* Main content */}
        <main className="flex-1 overflow-auto p-4 md:p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
